import { Skeleton } from '@/shared/ui/skeleton';
import { alertesSante, refCourte } from '@/features/bridge/bridge-etat';
import ErreurBloc from '@/features/bridge/components/ErreurBloc';

/**
 * Ce qu'on a promis à cet agrégateur part-il vraiment ?
 *
 * Deux lectures qui existaient côté serveur et qu'aucun écran n'affichait :
 *
 *   la sonde métier   un actif déclaré qui ne publie plus la grandeur annoncée,
 *                     un périmètre entièrement injoignable, une clé sans actif
 *   les divergences   un actif piloté par un agrégateur et rattaché à un autre sur
 *                     sa fiche client : une révocation oubliée, le plus souvent
 *
 * La sonde de fond alerte déjà sur les deux ; l'onglet dit la même chose, lue au même
 * endroit (`sonde_bridge.etat_bridge`), pour que l'écran et l'alerte ne se contredisent
 * jamais. Depuis le 28/09, cette lecture est cloisonnée au sous-compte de l'appelant.
 *
 * Les deux lectures sont faites par la page, qui en tire le compte affiché sur le nom
 * de l'onglet : un onglet qui ne compte ses alertes qu'une fois ouvert n'avertit
 * personne.
 */
const STATUT = {
  produit: { libelle: 'les mesures promises partent', teinte: 'text-emerald-700' },
  muet: { libelle: 'silence', teinte: 'text-red-700' },
  inapplicable: { libelle: 'pas encore raccordé', teinte: 'text-neutral-500' },
};

export default function OngletSante({
  agregateur, label, sante, divergences, erreurSante, erreurDiv,
}) {
  const volet = sante?.agregateurs?.[agregateur];
  const { muettes, ecarts } = alertesSante(sante, divergences, agregateur);

  const statut = STATUT[volet?.statut] || STATUT.inapplicable;

  return (
    <div className="space-y-5" data-testid="onglet-sante">
      <section aria-labelledby="sonde-titre" className="space-y-2">
        <h3 id="sonde-titre" className="text-[14px] font-semibold text-neutral-900">Mesures promises</h3>
        <ErreurBloc erreur={erreurSante} testid="bridge-sante-erreur" />
        {!sante && !erreurSante && <Skeleton className="h-20 w-full" />}
        {sante && (
          <div className="rounded-md border border-neutral-200 px-4 py-3" data-testid="bridge-sante-volet">
            <p className={`text-[13.5px] font-medium ${statut.teinte}`}>
              {volet ? statut.libelle : `${label} n'a aucune clé active : rien à surveiller`}
            </p>
            {volet?.precision && <p className="mt-0.5 text-[13px] text-neutral-600">{volet.precision}</p>}
            {muettes.length > 0 && (
              <ul className="mt-2 space-y-1 text-[12.5px]">
                {muettes.map((m) => (
                  <li key={m.asset_id} className="flex flex-wrap gap-x-2">
                    <code title={m.ref}>{refCourte(m.ref)}</code>
                    <span className="text-neutral-600">{m.manque}</span>
                    {m.promises?.length > 0 && (
                      <span className="text-neutral-400">({m.promises.join(', ')})</span>
                    )}
                  </li>
                ))}
              </ul>
            )}
          </div>
        )}
      </section>

      <section aria-labelledby="divergences-titre" className="space-y-2">
        <h3 id="divergences-titre" className="text-[14px] font-semibold text-neutral-900">
          Piloté par l&apos;un, rattaché à l&apos;autre
        </h3>
        <ErreurBloc erreur={erreurDiv} testid="bridge-divergences-erreur" />
        {!divergences && !erreurDiv && <Skeleton className="h-16 w-full" />}
        {divergences && ecarts.length === 0 && (
          <p className="text-[13px] text-neutral-500" data-testid="bridge-divergences-vide">
            Aucun écart : chaque actif déclaré à {label} appartient à un client rattaché à {label}.
          </p>
        )}
        {ecarts.length > 0 && (
          <div className="overflow-x-auto rounded-md border border-neutral-200" data-testid="bridge-divergences">
            <table className="w-full min-w-[520px] text-[13px]">
              <thead className="bg-neutral-50 text-left text-[12px] text-neutral-500">
                <tr><th className="px-3 py-2 font-normal">Client</th><th className="font-normal">Actif</th>
                  <th className="font-normal">Piloté par</th><th className="font-normal">Fiche client</th></tr>
              </thead>
              <tbody>
                {ecarts.map((e) => (
                  <tr key={`${e.pilote_par}-${e.asset_id}`} className="border-t border-neutral-100">
                    <td className="px-3 py-2">{e.client || '—'}</td>
                    <td>{e.type} <code className="text-[12px] text-neutral-500" title={e.ref}>{refCourte(e.ref)}</code></td>
                    <td>{e.pilote_par}</td>
                    <td>{e.facture_a || 'aucun agrégateur'}</td>
                  </tr>
                ))}
              </tbody>
            </table>
            <p className="border-t border-neutral-100 px-3 py-2 text-[12.5px] text-neutral-600">
              Si le client a changé d&apos;agrégateur, retirez la déclaration depuis l&apos;onglet
              Périmètre. Si c&apos;est voulu (un client chez deux agrégateurs), rien à faire.
            </p>
          </div>
        )}
      </section>
    </div>
  );
}
